/**
 * Inline Service Areas Editor
 * Click to edit service areas directly in the table with a popover
 */

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Button,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Check, Close, Edit, LocationOn } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import toast from 'react-hot-toast';
import driverService from '@/services/driverService';

const COMMON_SERVICE_AREAS = [
  'Lagos', 'Lekki', 'Victoria Island', 'Ikoyi', 'Ikeja', 'Surulere',
  'Ajah', 'Yaba', 'Maryland', 'Apapa', 'Festac', 'Banana Island',
  'Nairobi CBD', 'Westlands', 'Kilimani', 'Accra', 'East Legon',
];

interface InlineServiceAreasEditorProps {
  driverId: string;
  currentAreas: string[];
  onUpdate: () => void;
}

export function InlineServiceAreasEditor({
  driverId,
  currentAreas,
  onUpdate,
}: InlineServiceAreasEditorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [areas, setAreas] = useState<string[]>(currentAreas);
  const [saving, setSaving] = useState(false);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setAreas(currentAreas);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setAreas(typeof value === 'string' ? value.split(',') : value);
  };

  const handleSave = async () => {
    if (areas.length === 0) {
      toast.error('Select at least one service area');
      return;
    }

    setSaving(true);
    try {

      // Use service_locations to match backend expected field name
      const result = await driverService.updateDriverDetails(driverId, {
        service_locations: areas,
      });

      toast.success('Service areas updated');
      onUpdate();
      handleClose();
    } catch (error: any) {
      console.error('❌ Failed to update service areas:', error);
      toast.error(error.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          '&:hover': { opacity: 0.7 },
        }}
      >
        {currentAreas.length > 0 ? (
          <>
            {currentAreas.slice(0, 2).map((area, idx) => (
              <Chip key={idx} label={area} size="small" />
            ))}
            {currentAreas.length > 2 && (
              <Chip label={`+${currentAreas.length - 2}`} size="small" variant="outlined" />
            )}
            <Edit sx={{ fontSize: 14, color: 'text.secondary', ml: 0.5 }} />
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOn sx={{ fontSize: 16 }} />
            Click to set
          </Typography>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Service Areas
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Select areas</InputLabel>
            <Select
              multiple
              value={areas}
              onChange={handleChange}
              input={<OutlinedInput label="Select areas" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {COMMON_SERVICE_AREAS.map((area) => (
                <MenuItem key={area} value={area}>
                  {area}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleClose} startIcon={<Close />}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving || areas.length === 0}
              startIcon={saving ? <CircularProgress size={14} /> : <Check />}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
